import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Image from '../../Image';

const validateMedia = url => url.match(/\.(gif)$/) != null;

const SlideMedia = ({
  isActive,
  isCurrent,
  mediaUrl,
  thumbUrl,
  duration,
  altText,
  groupIndex,
  groupSize,
  isVisible,
}) => {
  const [isAnimated, setIsAnimated] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const slideRef = useRef(null);

  // Start gif after 1 second
  useEffect(() => {
    if (!isPlaying && isActive && isVisible) {
      const timeoutDuration = 1000;
      const timeOut = setTimeout(() => {
        setIsPlaying(true);
      }, timeoutDuration);
      return () => {
        clearTimeout(timeOut);
      };
    }
    return undefined;
  }, [isPlaying, isActive, isVisible]);

  // Stop gif after defined duration
  useEffect(() => {
    if (isPlaying && isActive && isVisible) {
      const timeoutDuration = +duration;
      const timer = setTimeout(() => {
        setIsPlaying(false);
      }, timeoutDuration);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isPlaying, isActive, isVisible, duration]);

  // Detect if media is gif by file extension
  useEffect(() => {
    setIsAnimated(validateMedia(mediaUrl));
    return () => {};
  }, [mediaUrl]);

  return (
    <Container ref={slideRef}>
      <SlideIndexIndicator>
        <ul>
          {Array.from(Array(groupSize)).map((groupEl, index) => (
            <SlideIndexItem
              active={index === groupIndex}
              // eslint-disable-next-line react/no-array-index-key
              key={`index-item-${index}-of-${groupSize}`}
            >
              {index === groupIndex && (
                <span>{`${index} of ${groupSize}`}</span>
              )}
            </SlideIndexItem>
          ))}
        </ul>
      </SlideIndexIndicator>
      <SlideContent
        // eslint-disable-next-line no-nested-ternary
        src={isPlaying && isActive && isVisible ? mediaUrl : ''}
        alt={altText}
        isVisible={isCurrent && isActive && isPlaying}
      />

      {isAnimated && (
        <SlideThumb
          src={thumbUrl}
          alt={`${altText} thumbnail`}
          isVisible={isCurrent}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  height: 100%;
`;

const SlideContent = styled.img`
  position: absolute;
  display: block;
  width: 100%;
  height: auto;
  z-index: 2;
  top: 0;
  left: 0;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
`;

const SlideThumb = styled(Image).attrs({
  responsiveWidth: 580,
})`
  position: relative;
  display: block;
  width: 100%;
  height: auto;
  z-index: 1;
`;

const SlideIndexIndicator = styled.div`
  display: block;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  ul {
    margin: 0;
    display: flex;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    background-color: rgba(59, 59, 59, 0.8);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    height: ${({ theme }) =>
      theme.isMobile
        ? theme.mobileRT.getWidthValue(30)
        : theme.webRT.getWidthValue(38)}px;
    justify-content: center;
    align-items: center;
    padding: 0
      ${({ theme }) =>
        theme.isMobile
          ? theme.mobileRT.getWidthValue(13)
          : theme.webRT.getWidthValue(16)}px;
  }
`;

const SlideIndexItem = styled.li`
  display: block;
  border: solid 1px white;
  border-radius: 50%;
  width: ${({ theme }) =>
    theme.isMobile
      ? theme.mobileRT.getWidthValue(6)
      : theme.webRT.getWidthValue(10)}px;
  height: ${({ theme }) =>
    theme.isMobile
      ? theme.mobileRT.getWidthValue(6)
      : theme.webRT.getWidthValue(10)}px;
  flex: 0 0 auto;
  background-color: ${({ active }) => (active ? 'white' : 'transparent')};
  margin: 0
    ${({ theme }) =>
      theme.isMobile
        ? theme.mobileRT.getWidthValue(6)
        : theme.webRT.getWidthValue(14)}px;
  span {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

SlideMedia.propTypes = {
  isActive: PropTypes.bool,
  isCurrent: PropTypes.bool,
  mediaUrl: PropTypes.string,
  altText: PropTypes.string,
  thumbUrl: PropTypes.string,
  duration: PropTypes.string,
  groupIndex: PropTypes.number,
  groupSize: PropTypes.number,
  isVisible: PropTypes.bool,
};

SlideMedia.defaultProps = {
  isActive: false,
  isCurrent: false,
  mediaUrl: '',
  altText: '',
  thumbUrl: '',
  duration: '',
  groupIndex: 0,
  groupSize: 0,
  isVisible: false,
};

export default SlideMedia;
